/**
 * inviteService — cross-area invites (Phase D, social.invites_feed).
 *
 * `game_join` routes a connection into a room; `lets_match` hands off to the
 * EXISTING match flow unchanged. TTL is enforced server-side; a background
 * sweep flips pending → expired.
 */

import { apiService } from './apiService';
import type { ConnectionInvite, InviteType } from '../../types/social';

const BASE = '/api/invites';

export interface SendInviteRequest {
  type: InviteType;
  toProfileId: string;
  /** Required when type === 'game_join'. */
  roomId?: string;
}

/** Room-join payload returned when a `game_join` invite is accepted. */
export interface InviteAcceptResult {
  invite: ConnectionInvite;
  /** Present for accepted `game_join` — routes the recipient into the room. */
  roomId?: string;
  /** Present for accepted `lets_match` — the resulting match id, if any. */
  matchId?: string;
}

export async function sendInvite(req: SendInviteRequest): Promise<ConnectionInvite | null> {
  const res = await apiService.post<ConnectionInvite>(BASE, req);
  if (res.error) throw new Error(res.error);
  return res.data ?? null;
}

export async function acceptInvite(inviteId: string): Promise<InviteAcceptResult | null> {
  const res = await apiService.post<InviteAcceptResult>(`${BASE}/${inviteId}/accept`);
  if (res.error) throw new Error(res.error);
  return res.data ?? null;
}

export async function declineInvite(inviteId: string): Promise<boolean> {
  const res = await apiService.post<{ success: boolean }>(`${BASE}/${inviteId}/decline`);
  if (res.error) throw new Error(res.error);
  return res.data?.success ?? true;
}

/** Pending, non-expired inbound invites. */
export async function getInboundInvites(): Promise<ConnectionInvite[]> {
  const res = await apiService.get<ConnectionInvite[]>(`${BASE}/inbound`);
  if (res.error) throw new Error(res.error);
  return res.data ?? [];
}

export const inviteService = {
  sendInvite,
  acceptInvite,
  declineInvite,
  getInboundInvites,
};

export default inviteService;
